import {
  CertificationRequest,
  CertificationsEnum,
} from "./certification.type";
import { ContentPaginatedInterface } from "./content.type";

export enum MaterialType {
  ALL = "ALL",
  DIRECT_GOODS = "DIRECT_GOODS",
  INDIRECT_GOODS = "INDIRECT_GOODS",
  SERVICES = "SERVICES",
}

/**
 * The lean references the backend `MaterialResponse` DTO
 * (`com.sm.spectum.payload.response.MaterialResponse`) actually serialises. Each mirrors one of
 * that DTO's nested `*Ref` static classes EXACTLY — nothing more. The whole point of the DTO (and
 * of these types) is that returning the raw `Material` entity used to leak the owning company's
 * taxId/email/phone/bankingInfos/contacts/aiSummary, the same graph nested under `deliveryPlace`
 * and every `tag`, and the `password` still mapped on `owner`/`user`. A FE consumer that can
 * compile against `material.company.taxId` or `material.user.phone` would read `undefined` at
 * runtime — the silent-blank class this narrowing exists to prevent. So the shapes below carry
 * only id-and-label fields, matched field-for-field to the Java.
 */
export interface CountryRef {
  id: string;
  name: string;
  nameEn: string;
}

export interface MaterialCompanyRef {
  id: string;
  name: string;
  country: CountryRef | null;
}

export interface MaterialSiteRef {
  id: string;
  name: string;
  internalCode?: string;
  address?: string;
  country?: CountryRef | null;
}

export interface MaterialUserRef {
  id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
}

export interface MaterialCategoryRef {
  id: string;
  name: string;
  nameEn: string;
  // The Java `CategoryRef.type` is a `CategoryType` enum whose values (DIRECT_GOODS /
  // INDIRECT_GOODS / SERVICES) are a subset of `MaterialType`; typed as `MaterialType` here to
  // match every consumer (the detail screen's market-info/countries hooks take `MaterialType`).
  type: MaterialType;
}

export interface MaterialCertificationRef {
  id: string;
  name: string;
  required: boolean;
  type: CertificationsEnum;
}

export interface MaterialTagRef {
  id: string;
  name: string;
  color: string;
  description?: string;
}

/**
 * The lean projection returned by EVERY `/material/**` endpoint — single reads, all search/list
 * endpoints (carried inside `MaterialCompany`), create, edit and bulk — and now also the Offers
 * flow (`MaterialOfferController` returns the `MaterialOfferResponse` DTO whose nested material is
 * this shape). Matched field-for-field to the backend `MaterialResponse` DTO. The one endpoint that
 * still serialises the raw `Material` entity is Chat (a fenced-off flow), typed with the
 * Chat-scoped `ChatMaterialResponse` in `chat.type.ts`, NOT this type.
 */
export interface MaterialResponse {
  active: boolean;
  brand: string;
  category: MaterialCategoryRef;
  certifications: MaterialCertificationRef[];
  company: MaterialCompanyRef;
  confident: boolean;
  confidentSpec: boolean;
  createdAt: string;
  deleted: boolean;
  deliveryFormat: string;
  deliveryPlace: MaterialSiteRef;
  details: string;
  ean: string;
  fileURL?: string;
  globalMaterialName: string;
  tags: MaterialTagRef[];
  id: string;
  measurementUnit: string;
  owner: MaterialUserRef;
  user: MaterialUserRef;
  comments: string;
  updatedAt: string;
}

export interface MaterialRequest {
  async: boolean;
  specifications: string;
  measurementUnit: string;
  ean: string;
  brand?: string;
  comments?: string;
  details?: string;
  category: { id: string };
  deliveryPlace: { id: string } | null;
  fileURL?: string;
  globalMaterialName: string;
  deliveryFormat?: string;
  confidentSpec: boolean;
  confident: boolean;
  certifications: CertificationRequest[];
  type: MaterialType;
  tags: { id: string }[];
}

export interface MaterialWithSuppliersResponse {
  material: MaterialResponse;
  activeOrdersQuantity: number;
  supplierValidatedQuantity: number;
  suppliersQuantity: number;
}

export type MaterialWithSuppliersPaginatedResponse =
  ContentPaginatedInterface<MaterialWithSuppliersResponse>;

export type MaterialPaginatedResponse =
  ContentPaginatedInterface<MaterialResponse>;
